import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  ListGroup,
  Form,
  InputGroup,
  Spinner,
} from "react-bootstrap";
import { X } from "react-bootstrap-icons";
import { Message } from "@/entities/message";
import { searchMessages } from "@/entities/search/api";
import { useMediaQuery } from "react-responsive";
import { useInfiniteSearch } from "@/shared/hooks";
import { MessageItem } from "../../search-results-list/message-item";

interface MessageSearchInputProps {
  chatId?: string;
  onSelectMessage: (chatId: string, messageId: string) => void;
  onClear?: () => void;
  placeholder?: string;
}

export interface MessageSearchInputHandle {
  clear: () => void;
  focus: () => void;
}

export const MessageSearchInput = forwardRef<
  MessageSearchInputHandle,
  MessageSearchInputProps
>(({ chatId, onSelectMessage, onClear, placeholder = "Поиск по сообщениям" }, ref) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [lastChatId, setLastChatId] = useState(chatId);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const searchFn = useCallback(
    ({ chatId,query, page }: { chatId?: string; query: string; page: number }) => searchMessages({ chatId, query, page }),
    []
  );

  const {
    query: searchInput,
    setQuery: setSearchInput,
    results: searchResults,
    loading: loadingSearch,
    hasMore,
    totalCount,
    containerRef,
    reset,
    handleSearch,
  } = useInfiniteSearch<Message>({ chatId, searchFn, debounceDelay: 400 });

  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        setTimeout(() => {
          setShowDropdown(false);
        }, 500);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // === Показ dropdown при вводе ===
  useEffect(() => {
    if (searchInput.length > 0) {
      setShowDropdown(true);
    }
  }, [searchInput]);

  const handleClear = () => {
    setShowDropdown(false);
    reset();
    setSearchInput("");
    onClear?.();
  };

  const handleSelect = (chatId: string, messageId: string) => {
    onSelectMessage(chatId, messageId);
    setSearchInput("")
    setShowDropdown(false);
    if (isMobile) {
      handleClear();
    }
  };


  // === Перезапрос при изменении chatId ===
  useEffect(() => {
    if (lastChatId && chatId && lastChatId !== chatId) {
      reset();
    }
  }, [chatId, lastChatId]);

  // Expose methods via ref
  useImperativeHandle(ref, () => ({
    clear: handleClear,
    focus: () => inputRef.current?.focus(),
  }));

  return (
    <>
      <div ref={wrapperRef} style={{ zIndex: 1001 }} className="position-relative w-100">
        <InputGroup>
          <Form.Control
            ref={inputRef}
            placeholder={placeholder}
            className={
              searchInput.length > 0 || loadingSearch ? "rounded-start-pill" : "rounded-5"
            }
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onFocus={() => {
              if (chatId !== lastChatId) {
                setLastChatId(chatId);
                handleSearch(searchInput, chatId)?.then(() => {
                  setShowDropdown(true)
                })
              }
              if (searchResults.length > 0) setShowDropdown(true);
            }}

            onBlur={() => {
              if (isMobile && !searchInput.trim()) {
                handleClear();
              }
            }}
          />

          {loadingSearch && (
            <InputGroup.Text>
              <Spinner size="sm" />
            </InputGroup.Text>
          )}

          {searchInput && (
            <InputGroup.Text
              onClick={handleClear}
              style={{ cursor: "pointer" }}
              className="rounded-end-pill"
            >
              <X />
            </InputGroup.Text>
          )}

        </InputGroup>


      </div>
      {showDropdown && searchResults.length > 0 && (
        <div
          ref={containerRef}
          className="position-absolute bg-white border rounded-bottom-4 shadow-sm mt-3 w-100 overflow-auto scrollbar-custom-y"
          style={{
            maxHeight: 400,
            zIndex: 1000,
          }}
        >
          {searchResults.length > 0 ? (
            <ListGroup variant="flush" style={{ marginTop: 23 }}>
              {searchResults.map((msg) => (
                <MessageItem key={msg._id} message={msg} query={searchInput} onSelectMessage={handleSelect} />
              ))}
            </ListGroup>
          ) : (
            !loadingSearch && (
              <div className="text-center text-muted py-3">Нет сообщений</div>
            )
          )}

          {loadingSearch && (
            <div className="text-center py-2">
              <Spinner animation="border" size="sm" />
            </div>
          )}
        </div>
      )}
    </>
  );
});

MessageSearchInput.displayName = "MessageSearchInput";